import { useMemo, useState } from "react"

import { Badge, Select } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { mutateOk, useZero } from "@/lib/zero"
import { SettingItem } from "../settings-switches/setting-item"

/** Linked repos in the order the agent treats them: the primary one first. */
export function linkedRepositoryNames(settings: {
	repositoryFullName?: string | null
	additionalRepositories?: readonly string[] | null
}): string[] {
	const names = [
		settings.repositoryFullName,
		...(settings.additionalRepositories ?? []),
	].filter((name): name is string => !!name)
	return [...new Set(names)]
}

/** What the picker shows when closed. */
export function linkedRepositoriesLabel(linked: readonly string[]): string {
	if (linked.length === 0) return "Select repositories"
	if (linked.length === 1) return linked[0]!
	return `${linked.length} repositories`
}

/** Removing the primary repo names the one that takes its place. */
export function toggleToastTitle(
	name: string,
	wasLinked: boolean,
	linked: readonly string[],
): string {
	if (!wasLinked) return `Linked ${name}`
	const nextPrimary = name === linked[0] ? linked[1] : undefined
	return nextPrimary
		? `Removed ${name}. ${nextPrimary} is now the primary repository.`
		: `Removed ${name}`
}

export function GithubRepositories({
	settingsId,
	repositoryFullName,
	additionalRepositories,
	installationRepos,
	loadingRepos,
}: {
	settingsId: string
	repositoryFullName: string | null | undefined
	additionalRepositories: readonly string[] | null | undefined
	installationRepos: readonly string[]
	loadingRepos: boolean
}) {
	const zero = useZero()
	const [busy, setBusy] = useState(false)

	const linked = linkedRepositoryNames({
		repositoryFullName,
		additionalRepositories,
	})

	// A linked repo the installation no longer lists still shows, so it can be unticked.
	const options = useMemo(() => {
		const names = [...linked]
		for (const name of installationRepos) {
			if (!names.some((n) => n.toLowerCase() === name.toLowerCase())) {
				names.push(name)
			}
		}
		return names.map((name) => ({
			value: name,
			label:
				name === linked[0] && linked.length > 1 ? (
					<span className="flex items-center gap-2">
						<span className="truncate">{name}</span>
						<Badge variant="light-gray">Primary</Badge>
					</span>
				) : (
					name
				),
		}))
	}, [installationRepos, linked])

	const toggle = async (name: string) => {
		if (busy) return
		const isLinked = linked.includes(name)
		setBusy(true)
		try {
			await mutateOk(
				zero.mutate(
					isLinked
						? mutators.githubSettings.removeRepository({
								id: settingsId,
								repositoryFullName: name,
							})
						: mutators.githubSettings.addRepository({
								id: settingsId,
								repositoryFullName: name,
							}),
				),
			)
			if (!isLinked) analytics.track("github-link-repo")
			toast.success({ title: toggleToastTitle(name, isLinked, linked) })
		} catch (error) {
			toast.error({
				title: isLinked ? `Couldn't remove ${name}` : `Couldn't link ${name}`,
				description: (error as Error).message,
			})
		} finally {
			setBusy(false)
		}
	}

	return (
		<SettingItem
			title="Linked repositories"
			description="The agent reads these repos."
			action={
				<Select
					name="linked-repositories"
					options={options}
					searchable
					multiple
					selectedValues={linked}
					onToggle={(name) => void toggle(name)}
					displayValue={
						loadingRepos && linked.length === 0
							? "Loading repositories..."
							: linkedRepositoriesLabel(linked)
					}
					searchPlaceholder="Search repositories"
					disabled={busy}
					className="w-56 truncate"
					selectClassName="dark:bg-gray-700/70"
					popoverClassName="w-80"
					popoverAlign="end"
				/>
			}
		/>
	)
}
